import * as React from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as Yup from 'yup';
import {PromotionType} from '@practicegenius/domain/src/data/promotions/promotion';
import {PgForm, usePgForm} from '@practicegenius/domain/src/hooks/usePgForm';
import {
  UnknownServerData,
  ApiErrorKind,
} from '@practicegenius/http-clients-js/src/types';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {
  FormConfirmationData,
  InfoFieldErrors,
  InfoFieldValues,
  IsMember,
} from '../types';
import {InterestedPartyType} from './InterestedParty';
import {coreApiClientNoAuth} from '../../../http/httpClientsJs';
import lang from '../lang';
import log from '../../../utils/logger';
import {ContactInfoForm} from './ContactInfoForm';

export type LandingPageSignUpFormValues = InfoFieldValues & {
  promotionType: PromotionType;
  catastrophicError: undefined;
  isMember: IsMember;
  isNotRealUser: boolean;
  honeyTimeElapsed: boolean;
};

const transformOutgoingIsMember = (isMember: IsMember): boolean | undefined => {
  switch (isMember) {
    case IsMember.YES:
      return true;
    case IsMember.NO:
      return false;
    default:
      return undefined;
  }
};

const transformOutgoingForm = (
  fields: LandingPageSignUpFormValues,
  promotionId: number
): UnknownServerData => {
  const isParent = fields.interestedParty === InterestedPartyType.myChild;

  return {
    firstname: isParent ? fields.childFirstName : fields.firstName,
    lastname: isParent ? fields.childLastName : fields.lastName,
    email: !isParent ? fields.email : null,
    cellphone: !isParent ? fields.phone : null,
    is_member: transformOutgoingIsMember(fields.isMember),
    signup_by_parent: isParent,
    responsible_firstname: isParent ? fields.firstName : null,
    responsible_lastname: isParent ? fields.lastName : null,
    responsible_email: isParent ? fields.email : null,
    responsible_cellphone: isParent ? fields.phone : null,

    referral: {
      promotion_id: promotionId,
    },
  };
};

const transformIncomingConfirmationData = (
  responseBody: UnknownServerData
): FormConfirmationData => {
  return {
    title: R.pathOr(
      '',
      ['included', 'confirmation_content', 'title'],
      responseBody
    ),
    body: R.pathOr(
      '',
      ['included', 'confirmation_content', 'body'],
      responseBody
    ),
  };
};

const transformIncomingErrors = (
  partyType: InterestedPartyType,
  errors: UnknownServerData
): InfoFieldErrors => {
  const isParent = partyType === InterestedPartyType.myChild;

  return pipe<InfoFieldErrors>(
    R.reject(R.isNil),
    R.map(R.head)
  )({
    firstName: isParent ? errors.responsible_firstname : errors.firstname,
    lastName: isParent ? errors.responsible_lastname : errors.lastname,
    phone: errors.cellphone || errors.responsible_cellphone,
    email: errors.email || errors.responsible_email,
    interestedParty: errors.signup_by_parent,
    childFirstName: isParent ? errors.firstname : undefined,
    childLastName: isParent ? errors.lastname : undefined,
  });
};

type Props = {
  promotionType: PromotionType;
  promotionId: number;
  practiceId: number;
  onSuccess: (confirmationData: FormConfirmationData) => void;
  // How long to wait before enabling form submission. Defaults to 5000. Setting to zero effectively overrides this
  enableFormAfterMs?: number;
  disableSubmit?: boolean;
};

export type LandingPageSignUpFormik = PgForm<LandingPageSignUpFormValues>;

const requiredNonEmpty = (schema: Yup.StringSchema = Yup.string()) =>
  schema.trim().ensure().required();

const validationSchema = Yup.object().shape({
  promotionType: Yup.string().oneOf([PromotionType.offer, PromotionType.event]),
  interestedParty: Yup.string().oneOf([
    InterestedPartyType.myself,
    InterestedPartyType.myChild,
  ]),
  firstName: requiredNonEmpty(
    Yup.string().label(lang.contactInfoFields.firstName)
  ),
  lastName: requiredNonEmpty(
    Yup.string().label(lang.contactInfoFields.lastName)
  ),
  phone: Yup.string().label(lang.contactInfoFields.phone).required(),
  email: requiredNonEmpty(Yup.string().label(lang.contactInfoFields.email)),
  isMember: Yup.string().when('promotionType', {
    is: PromotionType.offer,
    then: Yup.string().oneOf([IsMember.YES, IsMember.NO]).required(),
  }),
  childFirstName: Yup.string()
    .label(lang.contactInfoFields.childFirstName)
    .when('interestedParty', {
      is: InterestedPartyType.myChild,
      then: requiredNonEmpty(),
    }),
  childLastName: Yup.string()
    .label(lang.contactInfoFields.childLastName)
    .when('interestedParty', {
      is: InterestedPartyType.myChild,
      then: requiredNonEmpty(),
    }),
  isNotRealUser: Yup.boolean().equals([false]),
  honeyTimeElapsed: Yup.boolean().equals([true]),
});

const HONEY_TIME_INTERVAL = 5000;

export const LandingPageSignUpForm: React.FC<Props> = ({
  promotionType,
  promotionId,
  practiceId,
  onSuccess,
  enableFormAfterMs = HONEY_TIME_INTERVAL,
  disableSubmit = false,
}) => {
  const formik = usePgForm<LandingPageSignUpFormValues>({
    initialValues: {
      promotionType,

      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      interestedParty: InterestedPartyType.myself as InterestedPartyType,
      childFirstName: '',
      childLastName: '',

      isMember: IsMember.NULL,
      catastrophicError: undefined,
      isNotRealUser: false,
      honeyTimeElapsed: !enableFormAfterMs,
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      (
        await coreApiClientNoAuth.post(
          `practices/${practiceId}/lead/type:promotion?include=confirmation_content`,
          transformOutgoingForm(values, promotionId)
        )
      )
        .mapData(R.pipe(transformIncomingConfirmationData, onSuccess))
        .mapErr(err => {
          helpers.setSubmitting(false);

          switch (err.kind) {
            case ApiErrorKind.ValidationError:
              return helpers.setErrors(
                transformIncomingErrors(values.interestedParty, err.errors)
              );

            default:
              log.error('Submitting promotion form failed', err);
              return helpers.setErrors({
                catastrophicError: lang.form.catastrophicError,
              });
          }
        });
    },
  });

  // This enables the form after the provided honeyTime interval has elapsed
  React.useEffect(() => {
    if (enableFormAfterMs) {
      // We set the field touched immediately so it will appear in the errors list without needing to interact with the form
      // and forcing a validation run
      formik.setFieldTouched('honeyTimeElapsed');
      const honeyTimer = setTimeout(() => {
        formik.setFieldValue('honeyTimeElapsed', true);
      }, enableFormAfterMs);

      return () => clearTimeout(honeyTimer);
    }
    // We only want this effect to run on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOffer = formik.values.promotionType === PromotionType.offer;
  const formLang = isOffer ? lang.signUp : lang.contactMe;

  return (
    <form
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disableSubmit) formik.handleSubmit(e);
      }}
    >
      <ContactInfoForm
        title={formLang.title}
        subtitle={formLang.subtitle}
        buttonText={formLang.buttonText}
        formik={formik}
      />
      <Box mt={4} display="flex" justifyContent="center">
        <Typography
          variant="subtitle1"
          sx={theme => ({color: theme.palette.error.main})}
        >
          {formik.errors.catastrophicError}
        </Typography>
      </Box>
    </form>
  );
};
